import {ChangeDetectionStrategy, Component, Input, OnChanges} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TooltipModule} from 'ngx-bootstrap/tooltip';

/**
 * The (i) beside one card of an assembled board, and the working behind that card's figure.
 *
 * The card is one entry of `cards` from ComponentAllocation::getAssembledBoard() (an order line) or
 * getAssembledProductBoard() (a product, no line in view). Stock On Hand: counted like a normal product
 * of the part that runs out first — per part its stock on hand, what is allocated, what is available, how
 * many units that can build, and what is carried forward to the containers.
 * A container: per part what was carried forward from earlier, what it brings, the total, and what is
 * carried forward once it has assembled its Total — the orders allocated to it included (the API adds
 * their parts back, so allocating to the container changes nothing here).
 *
 * One component, so the Stock Allocation cards and the Summary tab explain a figure in exactly the
 * same words — two copies of this tooltip would drift the first time either was changed.
 */
@Component({
    selector: 'app-assembled-card-info',
    standalone: true,
    imports: [CommonModule, TooltipModule],
    // Eager, like the cards it sits in: Angular 22 defaults to OnPush.
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './assembled-card-info.component.html',
    styleUrl: './assembled-card-info.component.css'
})
export class AssembledCardInfoComponent implements OnChanges {
    /** One card of the board: the Stock On Hand card (key WAREHOUSE) or a container's. */
    @Input() card: any = null;
    /** The board's `unleashed` block. Only the Stock On Hand card is on Unleashed's figures, and only when they were used. */
    @Input() unleashed: any = null;
    @Input() uom: string = '';
    /** The icon is drawn in black, so a dark surface (the Stock Allocation page) turns it white. */
    @Input() dark = false;

    isWarehouse = false;
    components: any[] = [];
    rows: any[] = [];

    ngOnChanges(): void {
        this.isWarehouse = this.card?.key === 'WAREHOUSE';
        this.components = this.sortedComponents();
        this.rows = this.componentRows();
    }

    /**
     * Ordered by the recipe ratio, heaviest use first — the SAME order on every card.
     *
     * Sorting by whichever component was limiting put them in a different order on each card,
     * because the constraint changes as shipments land. That made the tooltips impossible to
     * compare: the eye had to re-find each component every time. The ratio never changes, so
     * this order never changes, and the limiting one is found by its badge instead of its
     * position. SKU breaks ties so it is fully deterministic.
     */
    private sortedComponents(): any[] {
        return [...(this.card?.components ?? [])].sort((a: any, b: any) =>
            (+b?.per_unit || 0) - (+a?.per_unit || 0) || String(a?.sku ?? '').localeCompare(String(b?.sku ?? '')));
    }

    /**
     * One row per component, as NUMBERS in fixed columns rather than a sentence.
     *
     * Prose was the problem: "160.20 available — 96.00 on this container, 64.20 from earlier stock"
     * is complete but unreadable, because the two figures that matter — what this container brings
     * and what was already there — are buried mid-sentence and land in a different place on every
     * row. In columns they line up and can be compared down the page at a glance.
     */
    private componentRows(): any[] {
        return this.components.map((component: any) => ({
            id: component?.component_product_id,
            sku: component?.sku,
            isLimiting: !!component?.is_limiting,
            perUnit: +component?.per_unit || 0,
            // Stock On Hand, as a normal product's: on hand, everything allocated to the part here (this
            // product's orders and every other claim), what is left, and the units that can build.
            onHand: +component?.on_hand || 0,
            allocated: (+component?.allocated_here || 0) + (+component?.allocated_other || 0),
            shelfAvailable: +component?.shelf_available || 0,
            canBuild: +component?.enough_for || 0,
            // A container: from earlier, what it brings, and the two together.
            earlierQty: +component?.carried_qty || 0,
            hereQty: +component?.qty_here || 0,
            totalQty: +component?.running_qty || 0,
            // Both: what is left once the card has assembled its units — where the next container starts.
            carriedForward: +component?.carried_forward || 0,
        }));
    }
}
